<template>
    <modal show-on-mount @hide="cancel()" :options="{
        close: {    
            onEsc: false,
            onBlur: false
        }
    }">
        <template #modal-title>
            <h3>{{resources.title}}</h3>
        </template>
        
        <template #modal-body>
            <div class="row form-group">
                <div class="col-12">
                    {{resources.description}}
                </div>
            </div>

            <div class="row form-group option-container">
                <div class="col-12 option-label">
                    {{resources.actionLabel}}
                </div>

                <div class="col-12 option-choice">
                    <selectable-button
                        :option="OPTIONS.ACTIONS.pause"
                        v-model="action"
                    >
                        {{resource('JourneyBuilder_DeactivateConfirmationPopup_Pause')}}
                    </selectable-button>

                    <selectable-button
                        :option="OPTIONS.ACTIONS.stop"
                        v-model="action"
                    >
                        {{resource('JourneyBuilder_DeactivateConfirmationPopup_Stop')}}
                    </selectable-button>
                </div>
            </div>

            <div class="row form-group option-container" v-if="action === OPTIONS.ACTIONS.pause">
                <div class="col-12 option-label">
                    {{resources.pauseDurationTypeLabel}}
                </div>

                <div class="col-12 option-choice">
                    <selectable-button
                        :option="OPTIONS.PAUSE_DURATION_TYPES.indefinite"
                        v-model="pauseDurationType"
                    >
                        {{resource('JourneyBuilder_DeactivateConfirmationPopup_PauseDurationIndefinite')}}
                    </selectable-button>

                    <selectable-button
                        :option="OPTIONS.PAUSE_DURATION_TYPES.period"
                        v-model="pauseDurationType"
                    >
                        {{resource('JourneyBuilder_DeactivateConfirmationPopup_PauseDurationPeriod')}}
                    </selectable-button>

                    <div class="d-inline-block">
                        <jira-style-time-period v-if="pauseDurationType === OPTIONS.PAUSE_DURATION_TYPES.period"
                                                v-model="pauseDuration"
                        />
                    </div>
                </div>
            </div>

            <div class="row form-group option-container" v-if="pauseDurationType === OPTIONS.PAUSE_DURATION_TYPES.indefinite || (pauseDuration && pauseDuration.seconds)">
                <div class="col-12 option-label">
                    {{resources.pauseFinishedObjectActionLabel}}
                </div>

                <div class="col-12 option-choice">
                    <selectable-button
                        :option="OPTIONS.PAUSE_FINISHED_OBJECT_ACTION.reactivate"
                        v-model="pauseFinishedObjectAction"
                    >
                        {{resource('JourneyBuilder_DeactivateConfirmationPopup_Reactivate')}}
                    </selectable-button>

                    <selectable-button
                        :option="OPTIONS.PAUSE_FINISHED_OBJECT_ACTION.stop"
                        v-model="pauseFinishedObjectAction"
                    >
                        {{resource('JourneyBuilder_DeactivateConfirmationPopup_Stop')}}
                    </selectable-button>
                </div>
            </div>

            <div class="row form-group option-container" v-if="pauseFinishedObjectAction === OPTIONS.PAUSE_FINISHED_OBJECT_ACTION.reactivate">
                <div class="col-12 option-label">
                    {{resources.pauseFinishedRecordActionLabel}}
                </div>

                <div class="col-12 option-choice">
                    <selectable-button
                        :option="OPTIONS.PAUSE_FINISHED_RECORDS_ACTION.process"
                        v-model="pauseFinishedRecordAction"
                    >
                        {{resource('JourneyBuilder_DeactivateConfirmationPopup_Process')}}
                    </selectable-button>

                    <selectable-button
                        :option="OPTIONS.PAUSE_FINISHED_RECORDS_ACTION.delete"
                        v-model="pauseFinishedRecordAction"
                    >
                        {{resource('JourneyBuilder_DeactivateConfirmationPopup_Delete')}}
                    </selectable-button>
                </div>
            </div>
            
            <div class="row form-group" v-if="isFinished">
                <div class="col-12 summary">
                    <template v-if="willDeleteRecords">
                        {{resources.summaryWithDelete}}
                    </template>
                    <template v-else>
                        {{resources.summaryWithPause}}
                    </template>
                    <br />
                    <br />
                    {{resources.confirmation}}
                </div>
            </div>
        </template>
        
        <template #modal-footer>
            <div class="w-100">
                <button class="float-left btn btn-danger" @click="cancel()">
                    <i class="fa fa-trash"></i> {{ resource('Button_Cancel') }}
                </button>

                <button v-if="isFinished" 
                        class="float-right btn btn-success"
                        @click="save()"
                >
                    <i class="fa fa-save"></i> {{ resource('Button_Save') }}
                </button>
            </div>
        </template>
    </modal>
</template>

<script>
import api from "@modules/JourneyBuilder/api";
import { timeUnit } from "@core/Util/time";
import sprintf from 'sprintf';
import { helpers, OPTIONS, RESOURCE_SETS } from './util';
import { getCurrentUser } from "@core/Util/current-user";

import resourceMixin from "@core/Util/Mixins/resources";

import Modal from "@core/Components/Util/Modal";
import JiraStyleTimePeriod from "@core/Components/Inputs/JiraStyleTimePeriod";

const SelectableButton = {
    props: ['value', 'option'],
    
    computed: {
        cssClass() {
            let cssClass = 'btn btn-outline-primary';
            if(this.value === this.option) {
                cssClass += ' selected';
            }
            return cssClass;
        }
    },
    
    render(createElement) {
        return createElement('button', {
            class: this.cssClass,
            
            on: {
                click: () => this.$emit('input', this.option)
            }
        }, [this.$slots.default]);
    }
};

const timeUnitLabelLookup = Object.keys(timeUnit)
    .reduce((lookup, label) => {
        lookup[timeUnit[label]] = label;
        return lookup;
    }, {});

export default {
    name: "DeactivationConfirmationPopup",
    
    components: {
        Modal,
        JiraStyleTimePeriod,

        SelectableButton
    },
    
    mixins: [resourceMixin],
    
    props: {
        /**
         * @type {"journey"|"connection"}
         */
        objectType: {
            type: String,
            required: true
        },
        
        objectId: {
            type: String,
            required: true
        }
    },
    
    data() {
        return {
            OPTIONS,
            
            affectedRecordsLoaded: false,
            affectedRecords: undefined,
            
            action: undefined,
            pauseDurationType: undefined,
            pauseDuration: undefined,
            pauseFinishedObjectAction: undefined,
            pauseFinishedRecordAction: undefined
        }
    },
    
    computed: {
        resources() {
            //Resources may differ slightly based on object type
            //Make a copy so we can format our resources with dynamic values
            const resources = {
                ...RESOURCE_SETS[this.objectType]
            };
            
            const affectedRecordsVal = this.affectedRecords !== undefined ? this.affectedRecords : '';
            
            resources.description = sprintf(resources.description, affectedRecordsVal);
            resources.pauseFinishedRecordActionLabel = sprintf(resources.pauseFinishedRecordActionLabel, affectedRecordsVal);
            resources.summaryWithDelete = sprintf(resources.summaryWithDelete, affectedRecordsVal);
            resources.summaryWithPause = sprintf(
                resources.summaryWithPause,
                affectedRecordsVal,
                helpers.formatPauseDurationLabel(this.pauseDurationType, this.pauseDuration)
            );
            
            return resources;
        },

        willDeleteRecords() {
            return helpers.willDeleteRecords(this.action, this.pauseFinishedObjectAction, this.pauseFinishedRecordAction);
        },
        
        isFinished() {
            return this.action === OPTIONS.ACTIONS.stop ||
                this.pauseFinishedObjectAction === OPTIONS.PAUSE_FINISHED_OBJECT_ACTION.stop ||
                this.pauseFinishedRecordAction;
        }
    },
    
    watch: {
        action() {
            //Reset all sub fields upon changing our action
            this.pauseDurationType = undefined;
            this.pauseDuration = undefined;
            this.pauseFinishedObjectAction = undefined;
            this.pauseFinishedRecordAction = undefined;
        },

        pauseDurationType(newType) {
            this.pauseDuration = undefined;
            
            //Finished actions are hidden prior to a period being configured thus we should reset them
            if(newType === OPTIONS.PAUSE_DURATION_TYPES.period) {
                this.pauseFinishedObjectAction = undefined;
                this.pauseFinishedRecordAction = undefined;
            }
        }
    },
    
    created() {
        this.loadAffectedRecords();
    },

    methods: {
        async loadAffectedRecords() {
            const { total } = await api.getObjectDeactivationAffectedRecordCount(
                this.objectType,
                this.objectId
            );
            
            this.affectedRecords = total;
            this.affectedRecordsLoaded = true;
        },
        
        getOptionButtonClass(associatedOption, selectedOption) {
            let cssClass = 'btn-outline-primary';
            
            if(associatedOption === selectedOption) {
                cssClass += ' selected';
            }
            
            return cssClass;
        },
        
        cancel() {
            this.$emit('cancel');
        },
        
        save() {
            const user = getCurrentUser();
            
            this.$emit('save', {
                action: this.action,
                
                pauseDurationType: this.pauseDurationType,
                pauseDuration: this.pauseDuration,
                pauseFinishedObjectAction: this.pauseFinishedObjectAction,
                pauseFinishedRecordAction: this.pauseFinishedRecordAction,
                
                date: new Date().toISOString(),
                
                userId: user.Id,
                userName: user.getFullName()
            })
        }
    }
}
</script>

<style scoped lang="scss">
    .option-container {
        display: flex;
        flex-flow: column;
        row-gap: 10px;
    }

    .selected {
        background-color: #007bff;
        border-color: #007bff;
        
        color: #ffffff;
    }
    
    .summary {
        font-weight: bold;
        margin-top: 20px;
    }
    
    ::v-deep {
        //Attempt to slightly shrink time input to prevent the control from jumping to another row upon changing unit
        .time-input {
            max-width: 12rem;
        }
    }
</style>